import {
  recoverServiceabilityField,
  errorMock,
  BACKUP_OFFER_KEY,
  PLACE_ID,
  IProduct,
  IProductOfferListByTech,
  checkIsApp,
  START_PRODUCT_HUB,
  ICoverageToolFlowType,
  IDXLNboProduct,
  DXL_NBO_CAMPAIGNS,
} from '@vfit/consumer/data-access';
import { API, getFromLocalStorageByKey, TechTypes } from '@vfit/shared/data-access';
import { useMutation } from 'react-query';
import { CustomOptions, INextError, LoggerInstance, nextClient } from '@vfit/shared/data-access';
import {
  IBackupOffer,
  ICharacteristic,
  IOfferingServiceabilityRequest,
  IOfferingServiceabilityResponse,
  IValidAddressList,
} from '@vfit/shared/models';
import { isAppLockin } from '@vfit/consumer/ibuyfixed';

const offeringServiceabilityService = (
  payload: IOfferingServiceabilityRequest,
  customOptions?: CustomOptions,
): Promise<IOfferingServiceabilityResponse> =>
  nextClient.post(API.OFFERING_SERVICEABILITY, payload, {
    ...(customOptions?.headers && { headers: customOptions.headers }),
    ...(customOptions?.silentLoginHeaders && {
      silentLoginHeaders: customOptions.silentLoginHeaders,
    }),
  });

const onSuccessServiceAbilityIbuy = (
  offerId: string,
  data: IOfferingServiceabilityResponse,
  allProducts: IProduct[],
  alternativeCmsIdList?: string[],
  alternativeCmsIdListByTech?: IProductOfferListByTech[],
  place_id?: string,
) => {
  if (place_id) {
    localStorage.setItem(PLACE_ID, place_id);
  }

  const alternativeProductList: { cmsId: string; offerId: string }[] = [];
  const alternativeOffersList: string[] = [];
  const dxlCampaignsFixed: IDXLNboProduct[] = getFromLocalStorageByKey(DXL_NBO_CAMPAIGNS);
  const startingOffer = getFromLocalStorageByKey('tProd') as IProduct;

  // --------------------
  // Build alternative products / offers
  // --------------------
  alternativeCmsIdList?.forEach((alternativeCmsId) => {
    const foundedProd = allProducts?.find(
      (el) => el?.cmsId === alternativeCmsId && (checkIsApp() || !el?.isLockInProduct),
    );

    if (foundedProd?.offerId) {
      alternativeProductList.push({
        cmsId: foundedProd.cmsId.toString(),
        offerId: foundedProd.offerId.toString(),
      });

      alternativeOffersList.push(foundedProd.offerId.toString());
    }
  });

  // --------------------
  // DXL + APP logic
  // --------------------
  if (dxlCampaignsFixed?.length > 0 && checkIsApp()) {
    // Intersezione semplice
    const dxlIds = dxlCampaignsFixed.map((el) => el?.bundlePO);

    const isMM = !startingOffer?.isLockInProduct;

    const prodottiFiltrati = data?.offeringServiceabilityItem?.filter((item) =>
      dxlIds.includes(item?.offering?.id),
    );

    const prodottiNonFiltrati = data?.offeringServiceabilityItem;

    // --------------------
    // 3. Try main offer
    // --------------------
    let foundedOffer: IBackupOffer | undefined = isMM
      ? prodottiNonFiltrati?.find((offer) => offer?.offering?.id === offerId)
      : prodottiFiltrati?.find((offer) => offer?.offering?.id === offerId);

    // --------------------
    // 4. Try alternative offers
    // --------------------
    if (!foundedOffer && alternativeOffersList.length > 0) {
      foundedOffer = prodottiFiltrati?.find((offer) =>
        alternativeOffersList.includes(offer?.offering?.id?.toString()),
      );
    }

    // --------------------
    // 5. Unserviceable check
    // --------------------
    const unserviceableCharacteristic = data?.serviceabilityCharacteristic?.some(
      (characteristic: ICharacteristic) =>
        characteristic.displayName === 'DisplayTechnologyName' &&
        characteristic.displayValue === '3G 4G',
    );

    // --------------------
    // 6. Persist results
    // --------------------
    if (foundedOffer && !unserviceableCharacteristic) {
      localStorage.setItem('offeringServiceability', JSON.stringify(data));

      const finalProdCmsId = alternativeProductList.find(
        (el) => el.offerId === foundedOffer?.offering?.id,
      )?.cmsId;

      const finalProduct = allProducts?.find((prod) => prod.cmsId === finalProdCmsId);

      if (finalProduct) {
        localStorage.setItem('tProd', JSON.stringify(finalProduct));
      }

      if (startingOffer?.offerId?.toString() === foundedOffer?.offering?.id) {
        localStorage.setItem(
          BACKUP_OFFER_KEY,
          JSON.stringify({
            offeringId: foundedOffer.offering.id,
            cmsId: startingOffer.cmsId || '',
          }),
        );
      } else {
        localStorage.setItem(
          BACKUP_OFFER_KEY,
          JSON.stringify({
            offeringId: foundedOffer.offering.id,
            cmsId: finalProdCmsId || '',
          }),
        );
      }
    }

    if (!foundedOffer && checkIsApp()) {
      localStorage.setItem('koCoverageIadd', JSON.stringify('true'));
    }
  }

  const originalTechType: TechTypes = recoverServiceabilityField(
    data?.serviceabilityCharacteristic || [],
    'Technology',
  ) as TechTypes;

  const idByTech = alternativeCmsIdListByTech?.filter(
    (el) => el?.technology.toLowerCase() === originalTechType?.toLowerCase(),
  );
  const allLockinProducts = allProducts?.filter((el) => el?.isLockInProduct);
  const lockinIds = allLockinProducts?.map((el) => el?.cmsId);
  const isTechFWA: boolean = originalTechType?.toLowerCase().includes('fwa');
  const isOutdoor: boolean = isTechFWA
    ? originalTechType?.toLowerCase().includes('outdoor')
    : false;
  localStorage.setItem('isTechFWA', JSON.stringify(isTechFWA));
  localStorage.setItem('isOutdoor', JSON.stringify(isOutdoor));
  localStorage.setItem('techOffer', JSON.stringify(originalTechType));

  if (
    !isAppLockin() &&
    idByTech?.length > 0 &&
    (checkIsApp() || !lockinIds?.includes(idByTech?.[0]?.id))
  ) {
    const findByTechCms = allProducts?.find((el) => el?.cmsId === idByTech?.[0]?.id);
    const foundedOffer = data?.offeringServiceabilityItem.find(
      (offer: IBackupOffer) => offer?.offering?.id === findByTechCms?.offerId?.toString(),
    );
    localStorage.setItem('offeringServiceability', JSON.stringify(data));
    localStorage.setItem(
      BACKUP_OFFER_KEY,
      JSON.stringify({
        offeringId: foundedOffer?.offering?.id,
        cmsId:
          alternativeProductList?.find?.((el) => el?.offerId == foundedOffer?.offering?.id)
            ?.cmsId || '',
      }),
    );

    const startProductTechOverrideHub = window?.location?.href;
    localStorage.setItem(START_PRODUCT_HUB, startProductTechOverrideHub);
  } else if (!isAppLockin()) {
    let foundedOffer = data?.offeringServiceabilityItem?.find(
      (offer: IBackupOffer) => offer?.offering?.id === offerId,
    );
    // alternative product
    if (!foundedOffer && alternativeOffersList?.length > 0) {
      alternativeOffersList?.forEach((aOL) => {
        const foundedAmdOffer = (
          data as IOfferingServiceabilityResponse
        )?.offeringServiceabilityItem?.find(
          (backupOffer: IBackupOffer) => aOL?.toString() === backupOffer?.offering?.id?.toString(),
        );
        if (foundedAmdOffer && !foundedOffer) foundedOffer = foundedAmdOffer;
      });
    }
    const unserviceableCharacteristic = data?.serviceabilityCharacteristic?.filter(
      (characteristic: ICharacteristic) =>
        characteristic.displayName === 'DisplayTechnologyName' &&
        characteristic.displayValue === '3G 4G',
    );

    if (foundedOffer && !unserviceableCharacteristic[0]) {
      localStorage.setItem('offeringServiceability', JSON.stringify(data));
      localStorage.setItem(
        BACKUP_OFFER_KEY,
        JSON.stringify({
          offeringId: foundedOffer?.offering?.id,
          cmsId:
            alternativeProductList?.find?.((el) => el?.offerId === foundedOffer?.offering?.id)
              ?.cmsId || '',
        }),
      );
    }
  }
};

export const useServiceabilityAddressMutation = (
  coverageType: ICoverageToolFlowType,
  offerId: string,
  allProducts: IProduct[],
  alternativeCmsIdList?: string[],
  alternativeCmsIdListByTech?: IProductOfferListByTech[],
  place_id?: string,
  isNewParallelCheck?: boolean,
) => {
  // isNewParallelCheck -> https://vodafone-it.atlassian.net/jira/software/c/projects/DES/boards/611?search=vignotto&selectedIssue=DES-22843
  return useMutation(
    'offeringServiceability',
    (validatedAddress: IValidAddressList) =>
      offeringServiceabilityService({
        ...(coverageType != 'FASTWEB' && {
          doFetchFastWebTechnologyFlag: coverageType === 'AMDOCS_FW',
        }),
        ...(isNewParallelCheck && {
          invokeParallelCoverageCheck: isNewParallelCheck ? true : false,
        }),
        serviceabilityAddress: validatedAddress,
      }),
    {
      onSuccess: (data: IOfferingServiceabilityResponse) => {
        localStorage.setItem('offeringServiceability', JSON.stringify(data));
        onSuccessServiceAbilityIbuy(
          offerId,
          data,
          allProducts,
          alternativeCmsIdList,
          alternativeCmsIdListByTech,
          place_id,
        );
      },
      onError: (error: INextError) => {
        LoggerInstance.error(`ERROR - offeringServiceability: `, error);
        localStorage.setItem(
          `offeringServiceability`,
          JSON.stringify(errorMock(`offeringServiceability`)),
        );
      },
    },
  );
};

export const useServiceabilityAddressMutationIADD = (
  coverageType: ICoverageToolFlowType,
  isNewParallelCheck?: boolean,
) => {
  // isNewParallelCheck -> https://vodafone-it.atlassian.net/jira/software/c/projects/DES/boards/611?search=vignotto&selectedIssue=DES-22843
  return useMutation(
    'offeringServiceability',
    (validatedAddress: IValidAddressList) =>
      offeringServiceabilityService({
        ...(coverageType != 'FASTWEB' && {
          doFetchFastWebTechnologyFlag: coverageType === 'AMDOCS_FW',
        }),
        ...(isNewParallelCheck && {
          invokeParallelCoverageCheck: isNewParallelCheck ? true : false,
        }),
        serviceabilityAddress: validatedAddress,
      }),
    {
      onSuccess: (data: IOfferingServiceabilityResponse) => {
        localStorage.setItem('offeringServiceability', JSON.stringify(data));
      },
      onError: (error: INextError) => {
        LoggerInstance.error(`ERROR - offeringServiceability: `, error);
        localStorage.setItem(
          `offeringServiceability`,
          JSON.stringify(errorMock(`offeringServiceability`)),
        );
      },
    },
  );
};

